import React from 'react';

interface ScoreboardDisplayProps {
  teamAScore: number;
  teamBScore: number;
  teamAHits: number;
  teamAErrors: number;
  teamBHits: number;
  teamBErrors: number;
  balls: number;
  strikes: number;
  outs: number;
  count: number;
  isTopInning: boolean;
  baseRunners: boolean[];
  teamAName?: string;
  teamAPrimaryColor?: string;
  teamASecondaryColor?: string;
  teamALogoUrl?: string;
  teamBName?: string;
  teamBPrimaryColor?: string;
  teamBSecondaryColor?: string;
  teamBLogoUrl?: string;
  onBallsChange?: (balls: number) => void;
  onStrikesChange?: (strikes: number) => void;
  textSizeReduced?: boolean;
}

// Simple function to determine if we should use white text
const shouldUseWhiteText = (backgroundColor: string) => {
  // Remove the # if present
  const hex = backgroundColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate perceived brightness (using relative luminance)
  // Values from WCAG 2.0
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  // Return true for dark backgrounds (use white text)
  return brightness < 128;
};

export function ScoreboardDisplay({
  teamAScore,
  teamBScore,
  teamAHits = 0,
  teamAErrors = 0,
  teamBHits = 0,
  teamBErrors = 0,
  balls,
  strikes,
  outs,
  count,
  isTopInning,
  baseRunners,
  teamAName = 'TEAM A',
  teamAPrimaryColor = '#D3E4FD',
  teamASecondaryColor = '#84CBE3',
  teamALogoUrl,
  teamBName = 'TEAM B',
  teamBPrimaryColor = '#D3E4FD',
  teamBSecondaryColor = '#84CBE3',
  teamBLogoUrl,
  onBallsChange,
  onStrikesChange,
  textSizeReduced = false,
}: ScoreboardDisplayProps) {
  const handleBallClick = () => {
    if (onBallsChange) {
      onBallsChange((balls + 1) % 4);
    }
  };

  const handleStrikeClick = () => {
    if (onStrikesChange) {
      onStrikesChange((strikes + 1) % 3);
    }
  };

  // Get text colors based on background colors
  const teamATextColor = shouldUseWhiteText(teamAPrimaryColor) ? 'white' : 'black';
  const teamBTextColor = shouldUseWhiteText(teamBPrimaryColor) ? 'white' : 'black';

  // Define text size classes based on whether we want reduced text
  const teamNameClass = textSizeReduced ? 
    'text-[1em] md:text-[1.5em] lg:text-[2em] font-bold tracking-wide' : 
    'text-[2em] md:text-[3em] lg:text-[4em] font-bold tracking-wide';
    
  const scoreClass = textSizeReduced ? 
    'text-[2em] md:text-[3em] lg:text-[4em] font-bold leading-none' : 
    'text-[4em] md:text-[6em] lg:text-[8em] font-bold leading-none';
    
  const inningClass = textSizeReduced ? 
    'text-white text-[1em] md:text-[1.5em] lg:text-[2em] font-bold whitespace-nowrap' : 
    'text-white text-[2em] md:text-[3em] lg:text-[4em] font-bold whitespace-nowrap';
    
  const ballsStrikesClass = textSizeReduced ? 
    'text-white text-[1em] md:text-[1.5em] lg:text-[2em] font-bold' : 
    'text-white text-[2em] md:text-[3em] lg:text-[4em] font-bold';
    
  const outsClass = textSizeReduced ? 
    'text-white text-[1em] md:text-[1.5em] lg:text-[2em] font-bold leading-none -mt-3' : 
    'text-white text-[2em] md:text-[3em] lg:text-[4em] font-bold leading-none mb-[0.5em]';

  // Define base runner container size based on whether we want reduced size
  const baseRunnerContainerClass = textSizeReduced ?
    'relative h-[3em] md:h-[4em] lg:h-[5em] mb-[0.25em] w-[6em] md:w-[8em] lg:w-[10em]' :
    'relative h-[6em] md:h-[8em] lg:h-[10em] mb-[0.25em] w-[12em] md:w-[16em] lg:w-[20em]';

  // Define border width based on whether we want reduced size
  // Increased border width for better visibility
  const baseBorderWidth = textSizeReduced ? 'border-[3px]' : 'border-4';

  // Define arrow sizes
  const arrowBorderSide = textSizeReduced ? 'border-l-[0.6em] border-r-[0.6em]' : 'border-l-[0.6em] border-r-[0.6em]';
  const arrowBorderTopBottom = textSizeReduced ? 'border-b-[0.9em] border-t-[0.9em]' : 'border-b-[0.9em] border-t-[0.9em]';

  // Define margin top for scores - add additional offset in fullscreen mode
  const teamScoreContainerClass = textSizeReduced ? 
    'absolute inset-0 flex flex-col items-center justify-center z-10 -mt-[10px] scale-[0.85]' : 
    'absolute inset-0 flex flex-col items-center justify-center z-10 scale-[0.9]';

  // Define text size classes for hits and errors
  const hitsErrorsClass = textSizeReduced ? 
    'text-[0.7em] md:text-[0.9em] lg:text-[1.1em] font-medium' : 
    'text-[1em] md:text-[1.2em] lg:text-[1.4em] font-medium';

  return (
    <div className="w-full -mt-8">
      <div className="relative w-full aspect-[4/1]">
        {/* Team A Block */}
        <div 
          className="absolute left-0 top-0 w-[29%] h-full overflow-hidden border-[0.15em]"
          style={{
            backgroundColor: teamAPrimaryColor,
            borderColor: teamASecondaryColor
          }}
        >
          {teamALogoUrl && (
            <img
              src={teamALogoUrl}
              alt="Away Team Logo"
              className="absolute inset-0 w-full h-full object-contain opacity-20"
            />
          )}
          <div className={teamScoreContainerClass}>
            <div className={teamNameClass} style={{ color: teamATextColor }}>
              {teamAName}
            </div>
            <div className={scoreClass} style={{ color: teamATextColor }}>
              {teamAScore}
            </div>
            <div className={hitsErrorsClass} style={{ color: teamATextColor }}>
              H: {teamAHits} E: {teamAErrors}
            </div>
          </div>
        </div>

        {/* Center Block */}
        <div className="absolute left-[29%] top-0 w-[42%] h-full bg-black flex flex-col justify-between py-[2%] px-[4%]">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-[0.5em]">
              <div className="flex flex-col gap-[0.15em]">
                <div className={`w-0 h-0 ${arrowBorderSide} border-l-transparent border-r-transparent border-b-[0.9em] ${isTopInning ? 'border-b-[#FF0606]' : 'border-b-[#333]'}`}></div>
                <div className={`w-0 h-0 ${arrowBorderSide} border-l-transparent border-r-transparent border-t-[0.9em] ${!isTopInning ? 'border-t-white' : 'border-t-[#333]'}`}></div>
              </div>
              <div className={inningClass}>
                {count === 99 ? "F" : count}
              </div>
            </div>
            <div className="flex gap-[0.5em]">
              <button 
                onClick={handleBallClick}
                className={`${ballsStrikesClass} cursor-pointer hover:text-yellow-400 transition-colors`}
              >
                {balls}
              </button>
              <span className={ballsStrikesClass}>-</span>
              <button 
                onClick={handleStrikeClick}
                className={`${ballsStrikesClass} cursor-pointer hover:text-yellow-400 transition-colors`}
              >
                {strikes}
              </button>
            </div>
          </div>
          
          <div className="flex flex-col items-center mt-2">
            <div className={outsClass}>{outs} OUTS</div>
            {/* Base Runners Display - Fixed for better visibility */}
            <div className={baseRunnerContainerClass}>
              {/* Second Base */}
              <div 
                className={`absolute left-1/2 -translate-x-1/2 top-[14px] w-[30%] aspect-square
                  ${baseRunners[1] ? 'bg-gradient-to-br from-[#FFB800] to-[#FF8A00]' : `${baseBorderWidth} border-white`} 
                  transform rotate-45 z-10`}
              />
              {/* Third Base */}
              <div 
                className={`absolute left-[10%] translate-y-[calc(75%+14px)] w-[30%] aspect-square
                  ${baseRunners[2] ? 'bg-gradient-to-br from-[#FFB800] to-[#FF8A00]' : `${baseBorderWidth} border-white`} 
                  transform rotate-45 z-10`}
              />
              {/* First Base */}
              <div 
                className={`absolute right-[10%] translate-y-[calc(75%+14px)] w-[30%] aspect-square
                  ${baseRunners[0] ? 'bg-gradient-to-br from-[#FFB800] to-[#FF8A00]' : `${baseBorderWidth} border-white`} 
                  transform rotate-45 z-10`}
              />
            </div>
          </div>
        </div>

        {/* Team B Block */}
        <div 
          className="absolute right-0 top-0 w-[29%] h-full overflow-hidden border-[0.15em]"
          style={{
            backgroundColor: teamBPrimaryColor,
            borderColor: teamBSecondaryColor
          }}
        >
          {teamBLogoUrl && (
            <img
              src={teamBLogoUrl}
              alt="Home Team Logo"
              className="absolute inset-0 w-full h-full object-contain opacity-20"
            />
          )}
          <div className={teamScoreContainerClass}>
            <div className={teamNameClass} style={{ color: teamBTextColor }}>
              {teamBName}
            </div>
            <div className={scoreClass} style={{ color: teamBTextColor }}>
              {teamBScore}
            </div>
            <div className={hitsErrorsClass} style={{ color: teamBTextColor }}>
              H: {teamBHits} E: {teamBErrors}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
